import React from 'react';
import { AlertTriangle, CheckCircle, Wrench } from 'lucide-react';
import { Button } from '../ui/button';

interface UrgentImprovementListProps {
  urgentCategories: Array<{ category: string; score: number; requirements: { completed: number; missing: number } } & any>;
  onGoToImprove: () => void;
}

export function UrgentImprovementList({ urgentCategories, onGoToImprove }: UrgentImprovementListProps) {
  const getScoreColor = (score: number): string => {
    if (score >= 75) return '#22c55e';
    if (score >= 50) return '#eab308';
    return '#ef4444';
  };

  const getScoreTextClass = (score: number): string => {
    if (score >= 75) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreSurfaceClass = (score: number): string => {
    if (score >= 75) return 'border-green-200 bg-green-50/50';
    if (score >= 50) return 'border-yellow-200 bg-yellow-50/50';
    return 'border-red-200 bg-red-50/50';
  };

  const getScoreIconSurfaceClass = (score: number): string => {
    if (score >= 75) return 'bg-green-100';
    if (score >= 50) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getScoreIconClass = (score: number): string => {
    if (score >= 75) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (!urgentCategories.length) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-l mb-2">Conformità eccellente</h3>
        <p className="text-muted-foreground">
          Tutte le categorie di accessibilità superano il 75% di conformità.
          Nessun miglioramento urgente richiesto al momento.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {urgentCategories.map((item, index) => {
        const total = item.requirements.completed + item.requirements.missing;
        const percentage = Math.round((item.requirements.completed / total) * 100);
        return (
          <div key={index} className={`flex items-center justify-between p-4 border rounded-lg ${getScoreSurfaceClass(item.score)}`}>
            <div className="flex items-center gap-4">
              <div className={`flex items-center justify-center w-12 h-12 rounded-lg ${getScoreIconSurfaceClass(item.score)}`}>
                <AlertTriangle className={`w-6 h-6 ${getScoreIconClass(item.score)}`} />
              </div>
              <div>
                <h4 className="font-medium text-foreground">{item.category}</h4>
                <div className="flex items-center gap-4 text-s text-muted-foreground mt-1">
                  <span>
                    Punteggio: <span className={`${getScoreTextClass(item.score)} font-medium`}>{item.score}%</span>
                  </span>
                  <span>•</span>
                  <span>
                    Mancanti: <span className="font-medium">{item.requirements.missing} requisiti</span>
                  </span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="w-16 h-2 bg-gray-200 rounded-full mb-2">
                <div className="h-2 rounded-full" style={{ width: `${percentage}%`, backgroundColor: getScoreColor(item.score) }}></div>
              </div>
              <p className="text-ss text-muted-foreground">{percentage}% completato</p>
            </div>
          </div>
        );
      })}

      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <h4 className="text-s font-medium text-yellow-800">Azione richiesta</h4>
            <p className="text-s text-yellow-700 mt-1">
              Queste categorie richiedono attenzione immediata per migliorare la conformità complessiva all'accessibilità.
              Considera di dare priorita ai miglioramenti in queste aree.
            </p>
          </div>
        </div>
      </div>
      <div className="mt-4 flex justify-center">
        <Button
          onClick={onGoToImprove}
          className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
        >
          <Wrench className="w-4 h-4 mr-2" />
          Vai a Migliora
        </Button>
      </div>
    </div>
  );
}
